import React from 'react'
import { BreadcrumbJsonLd, NextSeo } from 'next-seo';
import PicCont from '../components/PicCont';
import SinglePack from '../components/SinglePack';
import SimilarCats from '../components/SimilarCats';
import Carrousel from '../components/Carrousel';
import BGBlock from '../components/BGBlock';
import ProductList from '../components/ProductList';
import LandingCategories from '../components/LandingCategories';
import { useDispatch, useSelector } from 'react-redux';
import { wrapper } from '../store/configStore';
import { SERVER_IP } from '../store/middleware/api';
import ClientReviews from '../components/ClientReviews';
import NotSure from '../components/NotSure';
import PastEvents from '../components/PastEvents';
import { getFilteredProducts, productsReceived, productsSetFilters } from '../store/slices/entities/products';
import { getAllPackages, packagesReceived } from '../store/slices/entities/packages';
import landing from '../public/Pictures/projector_h.jpg'
import projectorHeroV2 from '../public/Pictures/categories-v2/projector-rental-hero-v2.jpg'
import movie from '../public/Pictures/movie_projector.jpg'
import projectorOverviewV2 from '../public/Pictures/categories-v2/projector-rental-overview-v2.webp'
import laser_projector from '../public/Pictures/laser_projector.jpg'
import outdoorProjectorOverviewV2 from '../public/Pictures/categories-v2/outdoor-projector-rental-overview-v2.webp'
import { GiBigDiamondRing } from "@react-icons/all-files/gi/GiBigDiamondRing";
import { BsFillBriefcaseFill } from "@react-icons/all-files/bs/BsFillBriefcaseFill";
import { FaBirthdayCake } from "@react-icons/all-files/fa/FaBirthdayCake";
import { FaSchool } from "@react-icons/all-files/fa/FaSchool";
import { FaPersonBooth } from "@react-icons/all-files/fa/FaPersonBooth";
import { FaChalkboard } from "@react-icons/all-files/fa/FaChalkboard";
import { HiUserGroup } from "@react-icons/all-files/hi/HiUserGroup";
import { BsMusicNoteBeamed } from "@react-icons/all-files/bs/BsMusicNoteBeamed";
import { RiMovie2Fill } from "@react-icons/all-files/ri/RiMovie2Fill";
import { ImPower } from '@react-icons/all-files/im/ImPower';
import { AiOutlineFullscreen } from '@react-icons/all-files/ai/AiOutlineFullscreen';
import { FaArrowsAltH } from '@react-icons/all-files/fa/FaArrowsAltH';
import { MdFullscreen } from "@react-icons/all-files/md/MdFullscreen";
import PageShell from '../components/design-system/foundations/PageShell';
import ServiceHero from '../components/design-system/marketing/ServiceHero';
import ServiceOverview from '../components/design-system/marketing/ServiceOverview';
import VisualDecisionGuide from '../components/design-system/marketing/VisualDecisionGuide';
import RentalFAQV2 from '../components/design-system/marketing/RentalFAQV2';
import QuotePop from '../components/DeferredQuotePopup';
import { SHOW_POPUP } from '../store/slices/ui';
import { FiMessageCircle } from '@react-icons/all-files/fi/FiMessageCircle';
import { FiFilm } from '@react-icons/all-files/fi/FiFilm';
import { FiMaximize2 } from '@react-icons/all-files/fi/FiMaximize2';
import { FiMonitor } from '@react-icons/all-files/fi/FiMonitor';
import { FiSun } from '@react-icons/all-files/fi/FiSun';
import * as gtag from '../lib/gtag';
import { compactCatalogProducts, compactPublicCatalogPackages } from '../utilities/catalogPublicPayload';
import { compactPublicEvents } from '../utilities/eventPublicPayload';
import {
    getVisualFamilyBreadcrumbs,
    toBreadcrumbJsonLdItems,
    toHeroBreadcrumbs
} from '../utilities/visualFamilyBreadcrumbs';
import { requestPageData } from '../utilities/boundedPageData';

const projectorRentalOverviewContent = {
    id: 'projector-rental-overview',
    heading: 'Choose a projector and screen for your Miami event',
    body: [
        'Compare professional projector rentals by brightness, resolution, throw distance, and compatible screen size. The right combination depends on the room, audience, content, and amount of ambient light.',
        'Select a projector online, choose your dates, and compare warehouse pickup with professional delivery and setup. Our AV team can also plan a complete projection system for conferences, presentations, screenings, and outdoor movie events.'
    ],
    guide: {
        heading: 'Select the perfect projector for your event',
        summary: 'Our range of professional projectors is designed to meet any event requirement, from conferences to movie nights. Finding the perfect projector rental is now simpler than ever.',
        body: [
            <p key='projector-selection-attributes'>Tailor your selection by choosing <strong>Lumens</strong> (projection power), <strong>Resolution</strong>, <strong>Throw Distance</strong>, <strong>Price</strong>, and more.</p>,
            <p key='projector-selection-process'>Once you&apos;ve made your choice, select <strong>&quot;Choose Dates &amp; Rent Online&quot;</strong>. You can then choose warehouse pickup or delivery and setup before entering the dates needed to show real availability.</p>
        ]
    },
    media: {
        src: projectorOverviewV2,
        alt: 'Professional projectors and presentation screen installed in a modern conference venue'
    }
}

const outdoorProjectorOverviewContent = {
    id: 'outdoor-projector-rental-packages',
    density: 'compact',
    headingLevel: 'h3',
    heading: 'Projector and screen packages for outdoor movie events',
    body: [
        'Outdoor projection requires the right screen size, viewing position, and light-control plan. For the best image, schedule the program after sunset and keep direct venue lighting away from the screen.',
        <p key='quality'>Choose a configured <strong>projector and screen</strong> package for a backyard movie night, community screening, or private event. Our team can help confirm brightness, power, weather protection, and professional setup requirements for the venue.</p>
    ],
    media: {
        src: outdoorProjectorOverviewV2,
        alt: 'Audience watching a bright outdoor projection in a covered Miami venue at night',
        position: 'right center'
    }
}

export const getStaticProps = wrapper.getStaticProps(
    async ({ store, preview }) => {
        const [packageResponse, landingResponse, faqResponse, subCategoryResponse, productResponse] = await Promise.all([
            requestPageData({
              required: true,
              validateData: Array.isArray,
              url: `${SERVER_IP}/inventory/pack/tag`,
              params: {
                tag: 'projector'
              },
              label: 'projector packages for /projector-rental'
            }),
            requestPageData({
              url: `${SERVER_IP}/user/static-landing`,
              fallback: {
                newsletterevents: [],
                total_events: null
              },
              label: 'landing proof for /projector-rental'
            }),
            requestPageData({
              url: `${SERVER_IP}/faq`,
              params: {
                category: 'Projector'
              },
              fallback: [],
              label: 'FAQs for /projector-rental'
            }),
            requestPageData({
              url: `${SERVER_IP}/subcategory/name/Projector`,
              params: {
                cat: 'visuals'
              },
              fallback: {
                similar_categories: []
              },
              label: 'related categories for /projector-rental'
            }),
            requestPageData({
              required: true,
              validateData: Array.isArray,
              url: `${SERVER_IP}/inventory/product`,
              params: {
                msr: true,
                cat: 'Projector'
              },
              label: 'products for /projector-rental'
            })
        ])

        const products = Array.isArray(productResponse.data) ? productResponse.data : []
        const packages = Array.isArray(packageResponse.data) ? packageResponse.data : []
        store.dispatch({ type: productsReceived.type, payload: compactCatalogProducts(products) })
        store.dispatch({ type: productsSetFilters.type, payload: { static: true } })
        if (packages.length) store.dispatch({ type: packagesReceived.type, payload: compactPublicCatalogPackages(packages) })

        const { newsletterevents = [], total_events = null } = landingResponse.data || {}
        const usedFallback = [packageResponse, landingResponse, faqResponse, subCategoryResponse, productResponse]
            .some(response => response.usedFallback)

        return {
            props: {
                newsletterevents: compactPublicEvents(newsletterevents),
                total_events,
                subCate: subCategoryResponse.data,
                faq: faqResponse.data
            },
            revalidate: usedFallback ? 60 : 3600
        }
    }
);

const ProjectorRental = ({ newsletterevents, total_events, subCate, faq }) => {
    let products = useSelector(getFilteredProducts)
    let packages = useSelector(getAllPackages)
    const dispatch = useDispatch()

    const openQuote = () => {
        gtag.event({ action: 'quote_click', label: 'leads', value: 3, category: 'orders' })
        dispatch(SHOW_POPUP({
            key: 'quote',
            size: 'medium',
            body: <QuotePop />
        }))
    }
    const heroSteps = [
        { icon: 'search', title: 'Choose your projector', description: 'Compare brightness, resolution, and throw distance.' },
        { icon: 'calendar', title: 'Select dates', description: 'Tell us when you need it.' },
        { icon: 'truck', title: 'Pickup or delivery', description: 'Collect it or schedule professional setup.' },
        { icon: 'success', title: 'Event-ready visuals', description: 'Reliable projection with local AV support.' }
    ]
    const breadcrumbs = getVisualFamilyBreadcrumbs('/projector-rental')
    const selectionFactors = [
        {
            label: 'Room conditions',
            title: 'Brightness and ambient light',
            icon: FiSun,
            summary: 'Projector brightness is measured in lumens. Brighter rooms and larger screens generally need more output, while dark rooms can use lower-output systems efficiently.',
            points: ['Venue lighting and windows', 'Indoor or protected outdoor use', 'Screen size and audience distance']
        },
        {
            label: 'Physical layout',
            title: 'Throw distance and screen size',
            icon: FiMaximize2,
            summary: 'The available distance between the lens and screen determines whether a standard-, short-, or ultra-short-throw projector is appropriate.',
            points: ['Measure the usable projection distance', 'Keep sightlines and walkways clear', 'Match the projector to the screen format']
        },
        {
            label: 'Source compatibility',
            title: 'Content, connections, and legacy formats',
            icon: FiFilm,
            summary: 'Modern digital and laser projectors replace most older slide, overhead, and LCD projector requests. Tell us what source or physical media you have so we can confirm a compatible workflow.',
            points: ['Presentation laptop or media player', 'Video resolution and aspect ratio', 'Adapters, playback, and audio needs']
        }
    ]

    return <>
        <BreadcrumbJsonLd itemListElements={toBreadcrumbJsonLdItems(breadcrumbs)} />
        <NextSeo
            title='Projector & Screen Rental in Miami & South Florida'
            description='Rent projectors and screens for meetings, conferences, movie nights, and events. Compare brightness, throw distance, screen size, and delivery or pickup options.'
            canonical="https://miamisoundrental.com/projector-rental"
            twitter={{
                handle: '@miamisoundrent1',
                site: '@miamisoundrent1',
                cardType: 'summary_large_image',
            }}
            openGraph={{
                type: 'website',
                url: `https://miamisoundrental.com/projector-rental`,
                title: 'Projector & Screen Rental in Miami & South Florida',
                description: 'Rent projectors and screens for meetings, conferences, movie nights, and events. Compare brightness, throw distance, screen size, and delivery or pickup options.',
                images: [
                    {
                        url: 'https://miamisoundrental.com/Pictures/categories-v2/projector-rental-hero-v2.jpg',
                        width: 1915,
                        height: 821,
                        alt: 'Professional projector installed for a ballroom event by Miami Sound Rental',
                    },

                ],
                site_name: 'Miami Sound Rental',
            }}
        />
        <div className='categoryRental projectorRental'>
            <PageShell>
                <ServiceHero
                    breadcrumbs={toHeroBreadcrumbs(breadcrumbs)}
                    eyebrow='Projector rental · Miami & Fort Lauderdale'
                    title='Projector Rental in Miami'
                    description='Compare projectors by brightness, resolution, throw distance, screen size, and venue lighting for meetings, conferences, and screenings.'
                    image={{ src: projectorHeroV2, position: 'center' }}
                    imageAlt='Professional projector installed for a ballroom event by Miami Sound Rental'
                    primaryAction={{
                        label: 'Browse Projectors & Screens',
                        href: '#catalog',
                        icon: FiMonitor,
                        conversionKey: 'projector-rental.hero.browse-projectors',
                        channel: 'rental',
                        salesPath: 'self_service',
                        onClick: () => gtag.event({
                            action: 'catalog_click',
                            label: 'leads',
                            value: 2,
                            category: 'orders'
                        })
                    }}
                    secondaryAction={{
                        label: 'Request a Projection Plan',
                        icon: FiMessageCircle,
                        conversionKey: 'projector-rental.hero.talk-to-expert',
                        channel: 'quote',
                        salesPath: 'managed',
                        onClick: openQuote
                    }}
                    stackActionsOnMobile
                    processLabel='How projector rental works'
                    steps={heroSteps}
                />
            </PageShell>
            <PageShell>
                <ServiceOverview content={projectorRentalOverviewContent} />
            </PageShell>
            <PageShell>
                <VisualDecisionGuide
                    id='projector-selection-guide'
                    eyebrow='Projector selection guide'
                    heading='Choose a projector system that fits the room and content'
                    description='A projector is only one part of the system. Confirm these three factors before choosing dates so the image will be readable and the equipment will fit the venue.'
                    items={selectionFactors}
                />
            </PageShell>
            <section className='stageType white'>


                <ProductList
                            carousel={{
                    name: 'Projector Rentals',
                    url: 'https://miamisoundrental.com/projector-rental',
                    description: 'Projector Rentals'
                }}
                sortBy={{
                    Lumens: ['darkSlateBlue', <ImPower key='bolt' style={{ color: 'darkSlateBlue' }} />],
                    Resolution: ['green', <MdFullscreen key='bolt' style={{ color: 'green' }} />],
                    'Throw Distance': ['darkGoldenRod', <FaArrowsAltH key='group' style={{ color: 'darkGoldenRod' }} />],
                }} listName='Projector Catalog' anchor products={products.filter(p => p.categories?.includes('Visuals/Projector') && p.name.includes('Projector') && !p.name.includes('Screen'))} />
                <NotSure
                    eyebrow='Assisted projection planning'
                    title='Need delivery, installation, switching, or technical operation?'
                    description='Request a projection plan matched to the room, screen, content sources, and show-support requirements.'
                    quoteLabel='Request a Projection Plan'
                />
            </section>

            <LandingCategories ui='v2' loading='lazy' title className='padBottom padTop' options category='visuals' />
            <section className='stageType white'>


                <ProductList noTop sortBy={{
                    Lumens: ['darkSlateBlue', <ImPower key='bolt' style={{ color: 'darkSlateBlue' }} />],
                    'Screen Size': ['darkGoldenRod', <AiOutlineFullscreen key='group' style={{ color: 'darkGoldenRod' }} />],
                    Resolution: ['green', <MdFullscreen key='bolt' style={{ color: 'green' }} />],
                }} listName='Projectors & Screen Bundles' products={products.filter(p => p.categories?.includes('Visuals/Projector') && p.name.includes('Bundle'))} />

            </section>

            <section className='stageType'>



                <PageShell>
                    <ServiceOverview
                        content={outdoorProjectorOverviewContent}
                        supportingContent={
                            <Carrousel ui='v2' className='whitePacks' size={1} array={packages?.filter(p => p.tags?.includes('projector'))?.sort((a, b) => b.price - a.price).map((pack) => <SinglePack ui='v2' key={pack._id} pack={pack} />)} />
                        }
                    />
                </PageShell>


            </section>

            <section className='stageType'>
                <BGBlock src={landing} className='marginBottom'>
                    <h2 className="upper">Projector Screen Rentals</h2>
                    <p>Match the screen to the audience, room dimensions, and projector output. Our catalog includes multiple screen sizes, including 120&quot; and 180&quot; options. If you are unsure which projector can fill the selected screen from the available throw distance, ask our team to confirm the combination.
                        </p>
                </BGBlock>
                <ProductList noTop listName='Projector Screens' products={products.filter(p => p.categories?.includes('Visuals/Projector') && p.name.includes('Projector Screen'))} />
            </section>
            <RentalFAQV2 questions={faq} headingId='projector-rental-faq-title' />
            <section className='stageType'>
                <BGBlock src={landing} className='iconList'>
                    <h2 className='upper'>Events for which Projector & Screen are rented</h2>
                    <div className="row">
                        <div className="col">
                            <div className="rowEvent"><HiUserGroup />Conferences</div>
                            <div className="rowEvent"><FaChalkboard />Seminars</div>
                            <div className="rowEvent"><FaPersonBooth />Trade Shows</div>
                        </div>
                        <div className="col">
                            <div className="rowEvent"><BsFillBriefcaseFill />Corporate Presentations</div>
                            <div className="rowEvent"><FaSchool />School Events</div>
                            <div className="rowEvent"><BsMusicNoteBeamed />Music Events</div>
                        </div>
                        <div className="col">
                            <div className="rowEvent"><RiMovie2Fill />Movie Night</div>
                            <div className="rowEvent"><FaBirthdayCake />Birthday Parties</div>
                            <div className="rowEvent"><GiBigDiamondRing />Weddings</div>
                        </div>
                    </div>
                </BGBlock>


            </section>

            <SimilarCats
                ui='v2'
                subCate={subCate}
                heading='Complete your Projector Rental'
                description='Finalize your projector rental with some of the following frequently rented together categories:'
            />
            <PastEvents ui='v2' filter={['corporate', 'presentation', 'conference']} newsletterevents={newsletterevents} total_events={total_events} />
            <ClientReviews ui='v2' filter={['trust']} />
        </div>
    </>
}

export default ProjectorRental
